<script setup lang="ts">
import { ref, computed, watch } from "vue";
import TinyUserThumb from "@/components/TinyUserThumb.vue";
import DefaultButton from "@/components/Buttons/DefaultButton.vue";
import {
  getBlurhashUrlSync,
  requestBlurhashUrl,
} from "@/helpers/blurhash/blurhash-url-queue";
import { DEFAULT_SHORT_MAX_DURATION_SECONDS } from "@/constants/shorts";
import type { ShortUploadItem } from "@/stores/modules/shorts/short-upload";

type Props = {
  upload: ShortUploadItem;
};

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: "retry", id: string): void;
  (e: "discard", id: string): void;
}>();

const hashUrl = ref<string>("");

const formattedDuration = computed(() => {
  const sec = props.upload.duration || DEFAULT_SHORT_MAX_DURATION_SECONDS;
  const s = Math.round(sec);
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}:${rem < 10 ? "0" : ""}${rem}`;
});

function loadBlurhash(hash?: string) {
  if (!hash) return;
  const sync = getBlurhashUrlSync(hash);
  if (sync) {
    hashUrl.value = sync;
  } else {
    requestBlurhashUrl(hash, (url) => {
      hashUrl.value = url;
    });
  }
}

watch(
  () => props.upload.blurhash,
  (newHash) => {
    loadBlurhash(newHash);
  },
  { immediate: true },
);
</script>

<template>
  <div
    class="group relative aspect-[9/16] w-full select-none overflow-hidden rounded-2xl border-2 border-black-dark bg-black-dark shadow-dark-hard-sm"
  >
    <!-- Background Image / Preview -->
    <img
      v-if="upload.posterUrl"
      :src="upload.posterUrl"
      alt="Short preview"
      class="h-full w-full object-cover brightness-75 filter"
    />
    <img
      v-else-if="hashUrl"
      :src="hashUrl"
      alt="Short preview"
      class="h-full w-full object-cover brightness-75 filter"
    />
    <div
      v-else
      class="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800"
    >
      <span class="text-3xl opacity-50">🎬</span>
    </div>

    <!-- Top Badges Row -->
    <div
      class="absolute left-2 right-2 top-2 z-10 flex items-center justify-between"
    >
      <!-- Uploading / Failed Badge -->
      <span
        v-if="upload.status === 'failed'"
        class="flex items-center gap-1 rounded-full border border-black-dark bg-kekkle-red px-2 py-0.5 text-[10px] font-black uppercase text-white shadow-sm"
      >
        <span>⚠️</span>
        <span>{{ $t("pages.shorts.upload_failed_title") }}</span>
      </span>
      <span
        v-else-if="upload.status === 'completed'"
        class="flex items-center gap-1 rounded-full border border-black-dark bg-green-600 px-2 py-0.5 text-[10px] font-black uppercase text-white shadow-sm"
      >
        <span>✓</span>
        <span>{{ $t("pages.shorts.posted_success") }}</span>
      </span>
      <span
        v-else
        class="flex items-center gap-1.5 rounded-full border border-black-dark bg-kekkle-yellow px-2 py-0.5 text-[10px] font-black uppercase text-black-dark shadow-sm"
      >
        <span
          class="inline-block h-2 w-2 animate-ping rounded-full bg-black-dark"
        />
        <span
          >{{ $t("pages.shorts.uploading") }}
          {{ Math.round(upload.progress) }}%</span
        >
      </span>

      <!-- Duration Pill -->
      <span
        class="rounded-full border border-white/20 bg-black-dark/60 px-2 py-0.5 text-[11px] font-bold text-white backdrop-blur-sm"
      >
        {{ formattedDuration }}
      </span>
    </div>

    <!-- Center Progress Ring when uploading -->
    <div
      v-if="upload.status !== 'failed'"
      class="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center p-4"
    >
      <div
        class="flex flex-col items-center gap-2 rounded-2xl border border-white/20 bg-black-dark/60 p-3 shadow-md backdrop-blur-sm"
      >
        <div class="relative flex h-12 w-12 items-center justify-center">
          <svg class="h-12 w-12 -rotate-90 transform" viewBox="0 0 36 36">
            <path
              class="text-white/20"
              stroke-width="3.5"
              stroke="currentColor"
              fill="none"
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            />
            <path
              :class="
                upload.status === 'completed'
                  ? 'text-green-500'
                  : 'text-kekkle-yellow'
              "
              class="transition-all duration-300"
              stroke-width="3.5"
              :stroke-dasharray="`${Math.max(5, Math.round(upload.progress))}, 100`"
              stroke-linecap="round"
              stroke="currentColor"
              fill="none"
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            />
          </svg>
          <span
            v-if="upload.status === 'completed'"
            class="absolute text-sm font-black text-green-400"
          >
            ✓
          </span>
          <span v-else class="absolute text-xs font-black text-white">
            {{ Math.round(upload.progress) }}%
          </span>
        </div>
      </div>
    </div>

    <!-- Failed Overlay with Retry / Discard actions -->
    <div
      v-else
      class="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black-dark/70 p-4 text-center backdrop-blur-sm"
    >
      <p class="mb-3 text-xs font-bold text-white drop-shadow">
        {{ upload.errorMessage || $t("pages.shorts.upload_failed") }}
      </p>
      <div class="flex flex-wrap items-center justify-center gap-2">
        <DefaultButton
          color="yellow"
          size="small"
          @click.stop="emit('retry', upload.id)"
        >
          🔄 {{ $t("pages.shorts.retry_upload") }}
        </DefaultButton>
        <DefaultButton
          color="red"
          size="small"
          @click.stop="emit('discard', upload.id)"
        >
          🗑️ {{ $t("pages.shorts.discard_upload") }}
        </DefaultButton>
      </div>
    </div>

    <!-- Bottom Gradient Overlay -->
    <div
      class="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex flex-col justify-end bg-gradient-to-t from-black-dark/90 via-black-dark/50 to-transparent p-3 pt-10 text-left"
    >
      <!-- Author Row -->
      <div class="flex items-center gap-1.5">
        <div class="short-avatar h-5 w-5 flex-shrink-0">
          <TinyUserThumb
            :uuid="upload.authUuid"
            class="h-full w-full rounded-full border border-white/80 object-cover"
          />
        </div>
        <span class="truncate text-xs font-bold text-white drop-shadow">
          {{ $t("pages.shorts.you") }}
        </span>
        <span class="text-[10px] text-gray-300 drop-shadow">
          • {{ $t("pages.shorts.uploading") }}
        </span>
      </div>

      <!-- Caption -->
      <p
        v-if="upload.caption"
        class="mt-1 line-clamp-2 text-xs font-medium text-white/95 drop-shadow"
      >
        {{ upload.caption }}
      </p>
    </div>
  </div>
</template>

<style scoped>
.short-avatar {
  width: 20px;
  height: 20px;
}

.short-avatar :deep(div) {
  width: 20px !important;
  height: 20px !important;
}

.short-avatar :deep(img) {
  width: 20px !important;
  height: 20px !important;
  object-fit: cover !important;
}
</style>
